import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  GridLegacy as Grid,
  TextField,
  Typography,
} from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import {
  Controller,
  FieldError,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldSetWrapper } from '~/components/GenericForm/FieldSetWrapper';
import { useApplicationConfig } from '~/data/applicationConfig';
import { EmploymentData } from '~/data/employee';
import { Enum } from '~/data/enums';
import { scheduledDateFormats } from '~/features/ScheduledEmploymentChanges/helpers';
import { EmploymentTypeComponent } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Employment/components/FormComponent/components/EmploymentTypeComponent';
import { useSxStyles } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Employment/components/FormComponent/useStyles';
import {
  Form,
  Restrict,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { convertDateTypeDatePickerProps } from '~/utils/helpers/date';

import {
  FixedTermReasonComponent,
  TerminationReasonComponent,
} from './components';

type Props = {
  terminationReasons: Enum[];
  fixedTermReasons: Enum[];
  trialPeriodOptions: Enum[];
  employmentTypeOptions: Enum[];
  employmentData: EmploymentData;
  loading: boolean;
  scheduleFormMethods: UseFormReturn<Form>;
  restrictField: Restrict;
  index: number;
  newEmpl: boolean;
  hasWorkEmail: boolean;
};

export const FormComponent = (props: Props) => {
  const {
    scheduleFormMethods,
    employmentData,
    trialPeriodOptions,
    fixedTermReasons,
    terminationReasons,
    employmentTypeOptions,
    restrictField,
    index,
    newEmpl,
    hasWorkEmail,
  } = props;

  const sxStyles = useSxStyles();

  const { control, setValue, getValues } = scheduleFormMethods;
  const { t } = useTranslation();

  const { applicationConfig } = useApplicationConfig();
  const enableManagerEditEmploymentStartDate =
    applicationConfig.fComConfig.mgrConfig.fComEnableMgrEditEmpStartDate;

  const isCurrentEmployment = index === 0;

  const disableTerminationFields =
    isCurrentEmployment && restrictField.notAllowEmpTermDate;

  const hasOnlyManagerAccess =
    restrictField.manager && !restrictField.inMyHRUnit;
  const disableStartAndArrivalDateFields =
    isCurrentEmployment &&
    hasOnlyManagerAccess &&
    !enableManagerEditEmploymentStartDate;

  const DEBOUNCE_TIMER_MS = 400;

  const getTextFieldProps = useCallback(
    (
      props: { error?: FieldError } & Partial<Omit<TextFieldProps, 'error'>>,
    ): Partial<TextFieldProps> => {
      const { error, slotProps, ...rest } = props;

      return {
        size: 'small',
        error: !!error,
        helperText: error?.message,
        slotProps: {
          ...slotProps,
          inputLabel: {
            shrink: true,
            ...slotProps?.inputLabel,
          },
        },
        ...rest,
      };
    },
    [],
  );

  const onEmploymentStartDateChangeSync = useMemo(
    () =>
      debounce((v: Date | null) => {
        if (v) {
          const empTrialPeriodValue = getValues(
            `schedule.${index}.employment.fEmplTrialPeriod`,
          );
          if (empTrialPeriodValue) {
            setValue(
              `schedule.${index}.employment.fEmplTrialPeriodEndDate`,
              moment(v, scheduledDateFormats.dotFormat)
                .add(parseInt(empTrialPeriodValue.charAt(1)), 'months')
                .subtract(1, 'days')
                .toDate(),
            );
          } else {
            setValue(
              `schedule.${index}.employment.fEmplTrialPeriodEndDate`,
              null,
            );
          }
          const detailsStartValue = getValues(
            `schedule.${index}.employmentDetails.fEmplDetailStartDate`,
          );
          const positioStartValue = getValues(
            `schedule.${index}.empPosition.fInternalPositionStartDate`,
          );
          if (newEmpl) {
            setValue(
              `schedule.${index}.employmentDetails.fEmplDetailStartDate`,
              v,
            );
            setValue(
              `schedule.${index}.empPosition.fInternalPositionStartDate`,
              v,
            );
          } else {
            //INFO: can't schedule new changes before the empl starts, that's why this sets the others to the same value if before they had earlier values
            if (moment(v).isAfter(positioStartValue)) {
              setValue(
                `schedule.${index}.empPosition.fInternalPositionStartDate`,
                v,
              );
            }
            if (moment(v).isAfter(detailsStartValue)) {
              setValue(
                `schedule.${index}.employmentDetails.fEmplDetailStartDate`,
                v,
              );
            }
          }
        }
        const dateArrivalValue = getValues(
          `schedule.${index}.employment.fEmplDateArrival`,
        );
        if (!v || moment(v).isAfter(dateArrivalValue)) {
          setValue(`schedule.${index}.employment.fEmplDateArrival`, v);
        }
      }, DEBOUNCE_TIMER_MS),
    [getValues, setValue, index, newEmpl],
  );

  const onTrialPeriodChangeSync = useMemo(
    () =>
      debounce((value: Enum | null) => {
        if (value) {
          const empStartDateValue = getValues(
            `schedule.${index}.employment.fEmplDateStart`,
          );
          setValue(
            `schedule.${index}.employment.fEmplTrialPeriodEndDate`,
            moment(empStartDateValue, scheduledDateFormats.dotFormat)
              .add(parseInt(value.code.charAt(1)), 'months')
              .subtract(1, 'days')
              .toDate(),
          );
        } else {
          setValue(
            `schedule.${index}.employment.fEmplTrialPeriodEndDate`,
            null,
          );
        }
      }, DEBOUNCE_TIMER_MS),
    [getValues, setValue, index],
  );

  const onLastDateOfEmploymentChangeSync = useMemo(
    () =>
      debounce((v: Date | null) => {
        setValue(`schedule.${index}.employment.fEmplTerminationDate`, v);
        if (v) {
          const detailsStartValue = getValues(
            `schedule.${index}.employmentDetails.fEmplDetailStartDate`,
          );
          const positioStartValue = getValues(
            `schedule.${index}.empPosition.fInternalPositionStartDate`,
          );
          //INFO: can't schedule new changes after the empl ends, that's why this sets the others to the same value if before they had later values
          if (moment(v).isBefore(positioStartValue)) {
            setValue(
              `schedule.${index}.empPosition.fInternalPositionStartDate`,
              v,
            );
          }
          if (moment(v).isBefore(detailsStartValue)) {
            setValue(
              `schedule.${index}.employmentDetails.fEmplDetailStartDate`,
              v,
            );
          }
        }
      }, DEBOUNCE_TIMER_MS),
    [getValues, setValue, index],
  );

  const TerminationFields = () => {
    const endOfEmploymentField = useWatch({
      control,
      name: `schedule.${index}.employment.fEmplLastDateEmployment`,
    });
    const hasEndOfEmployment = !!endOfEmploymentField;

    return (
      <>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Controller
            name={`schedule.${index}.employment.fEmplTerminationDate`}
            control={control}
            rules={{ required: hasEndOfEmployment }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const minDate: Date | undefined =
                index !== 0
                  ? moment(
                      getValues(
                        `schedule.${
                          index - 1
                        }.employment.fEmplLastDateEmployment`,
                      ),
                    )
                      .add(1, 'day')
                      .toDate()
                  : undefined;
              const maxDate: Date | undefined =
                endOfEmploymentField || undefined;

              return (
                <DatePicker
                  disabled={disableTerminationFields}
                  {...convertDateTypeDatePickerProps({
                    value,
                    onChange,
                    minDate,
                    maxDate,
                  })}
                  label={t('fLastDTemp')}
                  slotProps={{
                    textField: getTextFieldProps({
                      error,
                      required: hasEndOfEmployment,
                      fullWidth: true,
                    }),
                  }}
                />
              );
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TerminationReasonComponent
            control={control}
            index={index}
            terminationReasons={terminationReasons}
            getValues={getValues}
            disabled={disableTerminationFields}
            hasEndOfEmployment={hasEndOfEmployment}
          />
        </Grid>
      </>
    );
  };

  const ArrivalDateField = () => {
    const empStartDate = useWatch({
      control,
      name: `schedule.${index}.employment.fEmplDateStart`,
    });

    return (
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Controller
          name={`schedule.${index}.employment.fEmplDateArrival`}
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            const minDate: Date | undefined = empStartDate || undefined;

            return (
              <DatePicker
                disabled={disableStartAndArrivalDateFields}
                {...convertDateTypeDatePickerProps({
                  value,
                  onChange,
                  minDate,
                })}
                label={t('fArrivalDate')}
                slotProps={{
                  textField: getTextFieldProps({
                    error,
                    required: true,
                    fullWidth: true,
                  }),
                }}
              />
            );
          }}
        />
      </Grid>
    );
  };

  const TrialPeriodEndsField = () => {
    const trialPeriod = useWatch({
      control,
      name: `schedule.${index}.employment.fEmplTrialPeriod`,
    });
    const trialPeriodHasValue = !!trialPeriod;
    return (
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Controller
          name={`schedule.${index}.employment.fEmplTrialPeriodEndDate`}
          control={control}
          rules={{ required: trialPeriodHasValue }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DatePicker
              label={t('fTrialPeriodEndDateInputLabel')}
              {...convertDateTypeDatePickerProps({
                value,
                onChange,
              })}
              slotProps={{
                textField: getTextFieldProps({
                  error,
                  required: trialPeriodHasValue,
                  fullWidth: true,
                }),
              }}
            />
          )}
        />
      </Grid>
    );
  };

  return (
    <Grid container columnSpacing={2} rowSpacing={2}>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <EmploymentTypeComponent
          control={control}
          getValues={getValues}
          index={index}
          employmentTypeOptions={employmentTypeOptions}
        />
      </Grid>
      {index === 0 && (
        <>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Controller
              control={control}
              name={`schedule.${index}.fEmpContractNumber`}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  disabled={index !== 0}
                  value={value}
                  onChange={onChange}
                  label={t('fEmpContractNumber')}
                  size={'small'}
                  fullWidth={true}
                  {...getTextFieldProps({ error })}
                  data-testid="contract-number-input"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Controller
              control={control}
              name={`schedule.${index}.mainContract`}
              render={({ field: { onChange, value } }) => (
                <Box>
                  <FormControlLabel
                    control={<Checkbox data-testid="main-contract-checkbox" />}
                    checked={value || false}
                    onChange={onChange}
                    disabled={!hasWorkEmail || value === true}
                    label={t('fEmpMainContract')}
                  />
                  {value === false && !hasWorkEmail && (
                    <Typography variant={'caption'}>
                      {t('mainContractInstructions')}
                    </Typography>
                  )}
                </Box>
              )}
            />
          </Grid>
        </>
      )}
      {index === 0 && (
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Controller
            control={control}
            name={`schedule.${index}.fEmpPersonNumber`}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                disabled={restrictField.manager && !restrictField.inMyHRUnit}
                value={value}
                onChange={onChange}
                label={t('fEmpPersonNumber')}
                size={'small'}
                fullWidth={true}
                {...getTextFieldProps({ error })}
                data-testid="employee-number-input"
              />
            )}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <FieldSetWrapper label={t('starting')}>
          <Grid container columnSpacing={2} rowSpacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller
                name={`schedule.${index}.employment.fEmplDateStart`}
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => {
                  const minDate: Date | undefined =
                    index !== 0
                      ? moment(
                          getValues(
                            `schedule.${
                              index - 1
                            }.employment.fEmplLastDateEmployment`,
                          ),
                        )
                          .add(1, 'day')
                          .toDate()
                      : undefined;

                  return (
                    <DatePicker
                      label={t('fEmplDateStart')}
                      {...convertDateTypeDatePickerProps({
                        value,
                        onChange: (value, context) => {
                          onChange(value, context);
                          onEmploymentStartDateChangeSync(value);
                        },
                        minDate,
                      })}
                      disabled={disableStartAndArrivalDateFields}
                      slotProps={{
                        textField: getTextFieldProps({
                          error,
                          required: true,
                          fullWidth: true,
                        }),
                      }}
                    />
                  );
                }}
              />
            </Grid>
            <ArrivalDateField />
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller
                control={control}
                name={`schedule.${index}.employment.fEmplTrialPeriod`}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Autocomplete
                    options={trialPeriodOptions}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o.code === v.code}
                    onChange={(_, value) => {
                      onChange(value?.code || '');
                      onTrialPeriodChangeSync(value);
                    }}
                    value={
                      trialPeriodOptions.find((o) => o.code === value) || null
                    }
                    size={'small'}
                    data-testid="trial-period-autocomplete"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('fTrialPeriod')}
                        {...getTextFieldProps({ error })}
                        data-testid="trial-period-input"
                      />
                    )}
                    fullWidth={true}
                  />
                )}
              />
            </Grid>
            <TrialPeriodEndsField />
          </Grid>
        </FieldSetWrapper>
      </Grid>

      <Grid item xs={12}>
        <FieldSetWrapper label={t('ending')}>
          <Grid container columnSpacing={2} rowSpacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller
                name={`schedule.${index}.employment.fEmplPlannedEndDate`}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => {
                  const minDate: Date | undefined =
                    index !== 0
                      ? moment(
                          getValues(
                            `schedule.${
                              index - 1
                            }.employment.fEmplLastDateEmployment`,
                          ),
                        )
                          .add(1, 'day')
                          .toDate()
                      : undefined;

                  return (
                    <DatePicker
                      {...convertDateTypeDatePickerProps({
                        value,
                        onChange,
                        minDate,
                      })}
                      label={t('fEmplPlannedEndDate')}
                      slotProps={{
                        textField: getTextFieldProps({
                          error,
                          fullWidth: true,
                        }),
                      }}
                    />
                  );
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={8}
              md={6}
              lg={4}
              sx={sxStyles.fieldAndButtonLayout}
            >
              <Controller
                name={`schedule.${index}.employment.fEmplLastDateEmployment`}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => {
                  const minDate: Date | undefined =
                    getValues(`schedule.${index}.employment.fEmplDateStart`) ||
                    undefined;
                  const maxDateValue = getValues(
                    `schedule.${index + 1}.employment.fEmplDateStart`,
                  );
                  const maxDate: Date | undefined = !!maxDateValue
                    ? moment(maxDateValue).subtract(1, 'day').toDate()
                    : undefined;

                  return (
                    <DatePicker
                      disabled={disableTerminationFields}
                      {...convertDateTypeDatePickerProps({
                        value,
                        onChange: (value: Date | null, context) => {
                          onChange(value, context);
                          onLastDateOfEmploymentChangeSync(value);
                        },
                        minDate,
                        maxDate,
                      })}
                      label={t('fLastDEmpl')}
                      slotProps={{
                        textField: getTextFieldProps({ error }),
                      }}
                    />
                  );
                }}
              />
              {!restrictField.notAllowEmpTermDate && (
                <Button
                  onClick={() => {
                    setValue(
                      `schedule.${index}.employment.fEmplLastDateEmployment`,
                      null,
                      { shouldDirty: true },
                    );
                    setValue(
                      `schedule.${index}.employment.fEmplTerminationDate`,
                      null,
                      { shouldDirty: true },
                    );
                  }}
                  startIcon={<i className="fa fa-undo"></i>}
                  data-testid="emp-termination-date-clear-button"
                >
                  {t('laClear')}
                </Button>
              )}
            </Grid>
            <TerminationFields />
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FixedTermReasonComponent
                control={control}
                index={index}
                fixedTermReasons={fixedTermReasons}
                getValues={getValues}
              />
            </Grid>
          </Grid>
        </FieldSetWrapper>
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={control}
          name={`schedule.${index}.employment.fEmplInfo`}
          defaultValue={employmentData.fEmplInfo}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              inputProps={{ maxLength: 3000 }}
              fullWidth
              multiline
              value={value}
              onChange={onChange}
              label={t('laInfo')}
              size={'small'}
              data-testid="info-input"
              {...getTextFieldProps({ error })}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};
